import React, { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { useQuery } from 'convex/react';
import { DynamicDownloadForm } from './DynamicDownloadForm';
import { Id } from "../../convex/_generated/dataModel";
import { api } from '../../convex/_generated/api';
import { NavigationConfig } from '../../types/navigation';
import { Download, MessageCircle, Phone, X } from 'lucide-react';
import { Button } from '../ui/button';
import { DynamicLeadForm } from './DynamicLeadForm';

// Animation variants
const dropAnimation = {
  initial: { 
    scale: 0.3,
    opacity: 0,
    y: -20
  },
  animate: { 
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
      mass: 0.8
    }
  },
  exit: {
    scale: 0.3,
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const containerAnimation = {
  initial: { y: 50 },
  animate: { 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
      staggerChildren: 0.1
    }
  },
  exit: { 
    y: 50,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
};

// Default fallback values for CTA text
const DEFAULT_SECTION_CTAS = {
  hero: "Get Latest Price & Availability",
  highlights: "Book Private Meeting",
  gallery: "Book Consultation Now",
  overview: "Get Latest Price & Availability",
  paymentPlan: "Book Private Meeting",
  location: "Book Consultation Now",
  leadForm: "Book Consultation Now",
  faq: "Fast Response Guaranteed"
};

// Default fallback values for social proof messages
const DEFAULT_SECTION_SOCIAL_PROOF = {
  hero: "Priority Selection Open",
  highlights: "Get Floor Plan & Project Details",
  gallery: "Priority Selection Open",
  overview: "Get Floor Plan & Project Details",
  paymentPlan: "Priority Selection Open",
  location: "Get Floor Plan & Project Details",
  leadForm: "Fast Response Guaranteed",
  faq: "Expert Consultation Available"
};

interface StickyActionsProps {
  projectId: Id<"projects">;
  language?: string;
  downloadFormContent?: {
    title?: string;
    subtitle?: string;
    labels?: Record<string, string>;
    interestOptions?: { value: string; label: string }[];
    previousPurchasesOptions?: { value: string; label: string }[];
    investmentTimelineOptions?: { value: string; label: string }[];
    success?: {
      title?: string;
      description?: string;
    };
    error?: {
      title?: string;
      description?: string;
    };
    privacyText?: string;
  };
}

export default function StickyActions({ 
  projectId, 
  language = "en", 
  downloadFormContent 
}: StickyActionsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showBrochure, setShowBrochure] = useState(false);
  const [currentSection, setCurrentSection] = useState('hero');
  const { scrollY, scrollYProgress } = useScroll();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch navigation settings for contact information
  const navigationConfig = useQuery(api.landingPageSettings.getNavigationMenuItems, {
    projectId,
    languageCode: language,
  }) as NavigationConfig | undefined;

  // Track scroll position and visibility
  useMotionValueEvent(scrollY, "change", (latest) => {
    const heroHeight = window.innerHeight;
    const leadFormSection = document.getElementById('leadForm');
    const leadFormTop = leadFormSection?.getBoundingClientRect().top ?? 0;
    
    setIsVisible(latest > heroHeight && leadFormTop > 100);
  });

  // Track scroll progress for brochure CTA - Updated logic for 30-60% range
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setShowBrochure(latest >= 0.3 && latest <= 0.6);
  });

  // Get dynamic CTA texts and social proof messages with fallbacks
  const sectionCTAs = {
    hero: navigationConfig?.stickyActionTexts?.hero || DEFAULT_SECTION_CTAS.hero,
    highlights: navigationConfig?.stickyActionTexts?.highlights || DEFAULT_SECTION_CTAS.highlights,
    gallery: navigationConfig?.stickyActionTexts?.gallery || DEFAULT_SECTION_CTAS.gallery,
    overview: navigationConfig?.stickyActionTexts?.overview || DEFAULT_SECTION_CTAS.overview,
    paymentPlan: navigationConfig?.stickyActionTexts?.paymentPlan || DEFAULT_SECTION_CTAS.paymentPlan,
    location: navigationConfig?.stickyActionTexts?.location || DEFAULT_SECTION_CTAS.location,
    leadForm: navigationConfig?.stickyActionTexts?.leadForm || DEFAULT_SECTION_CTAS.leadForm,
    faq: navigationConfig?.stickyActionTexts?.faq || DEFAULT_SECTION_CTAS.faq
  };

  const sectionSocialProof = {
    hero: navigationConfig?.sectionSocialProof?.hero || DEFAULT_SECTION_SOCIAL_PROOF.hero,
    highlights: navigationConfig?.sectionSocialProof?.highlights || DEFAULT_SECTION_SOCIAL_PROOF.highlights,
    gallery: navigationConfig?.sectionSocialProof?.gallery || DEFAULT_SECTION_SOCIAL_PROOF.gallery,
    overview: navigationConfig?.sectionSocialProof?.overview || DEFAULT_SECTION_SOCIAL_PROOF.overview,
    paymentPlan: navigationConfig?.sectionSocialProof?.paymentPlan || DEFAULT_SECTION_SOCIAL_PROOF.paymentPlan,
    location: navigationConfig?.sectionSocialProof?.location || DEFAULT_SECTION_SOCIAL_PROOF.location,
    leadForm: navigationConfig?.sectionSocialProof?.leadForm || DEFAULT_SECTION_SOCIAL_PROOF.leadForm,
    faq: navigationConfig?.sectionSocialProof?.faq || DEFAULT_SECTION_SOCIAL_PROOF.faq
  };

  // Determine current section for dynamic CTA
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setCurrentSection(entry.target.id);
        }
      });
    }, { 
      threshold: 0.3,
      rootMargin: '-100px 0px -50% 0px'
    });

    // Observe all sections
    Object.keys(sectionCTAs).forEach(section => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sectionCTAs]);

  const handleWhatsAppClick = () => {
    if (!navigationConfig?.contactSettings) return;
    
    const { whatsappNumber, whatsappMessage } = navigationConfig.contactSettings;
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^\d]/g, '')}?text=${encodedMessage}`;
    
    // GTM Tracking - WhatsApp Click
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'whatsapp_click',
        'whatsapp_number': whatsappNumber,
        'property_id': projectId,
        'message_template': whatsappMessage,
        'click_location': 'sticky_actions',
        'lead_value': 120,
        'currency': 'AED'
      });
    }
    
    window.open(whatsappUrl, '_blank');
  };

  const handleOpenModal = () => {
    // GTM Tracking - Brochure Download Intent
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'brochure_download_intent',
        'property_id': projectId,
        'click_location': 'sticky_actions',
        'action': 'open_download_modal'
      });
    }
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFormSuccess = () => {
    // Optional: trigger brochure download or other actions

  };

  // Handle contact button click - scroll to lead form
  const handleContactClick = () => {
    // GTM Tracking - Lead Form Navigation
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'lead_form_navigation',
        'property_id': projectId,
        'click_location': 'sticky_actions',
        'current_section': currentSection,
        'action': 'scroll_to_lead_form'
      });
    }
    
    const leadFormSection = document.getElementById('leadForm');
    if (leadFormSection) {
      leadFormSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Don't show anything if navigation config is not loaded
  if (!navigationConfig) {
    return null;
  }

  const primaryButtonText = navigationConfig.ctaButtons?.primary || 'Download Brochure Now';

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            variants={containerAnimation}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed bottom-0 left-0 right-0 z-[999]"
          >
            {/* Backdrop with blur effect */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-background/80 backdrop-blur-sm border-t border-border"
            />
            
            <div className="relative max-w-7xl mx-auto px-4 py-3">
              <div className="flex flex-col gap-2">
                {/* First Row: Brochure Button */}
                <AnimatePresence>
                  {showBrochure && (
                    <motion.div
                      variants={dropAnimation}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="w-full"
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="relative w-full group"
                        onClick={handleOpenModal}
                      >
                        <span className="absolute inset-0 w-full h-full bg-primary/10 rounded-lg blur-lg group-hover:blur-xl transition-all duration-300 opacity-75" />
                        <span className="relative flex items-center justify-center gap-2 px-4 py-2.5 bg-background border-2 border-primary text-primary rounded-lg font-medium shadow-lg hover:shadow-primary/20 transition-all duration-300 w-full">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            className="w-5 h-5"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {primaryButtonText}
                        </span>
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Second Row: WhatsApp and Primary CTA */}
                <div className="flex items-center gap-3">
                  {/* WhatsApp Button */}
                  <motion.div variants={dropAnimation}>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleWhatsAppClick}
                      className="relative group"
                    >
                      <span className="absolute inset-0 w-full h-full bg-[#25D366] rounded-full blur-lg group-hover:blur-xl transition-all duration-300 opacity-75" />
                      <span className="relative block p-3 bg-[#25D366] text-white rounded-full shadow-lg hover:shadow-[#25D366]/50 transition-all duration-300">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="currentColor" 
                          viewBox="0 0 24 24" 
                          className="w-6 h-6"
                        >
                          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                        </svg>
                      </span>
                    </motion.button>
                  </motion.div>

                  {/* Primary CTA */}
                  <motion.div variants={dropAnimation} className="flex-1">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative w-full group"
                      onClick={handleContactClick}
                    >
                      <span className="absolute inset-0 w-full h-full bg-primary rounded-lg blur-lg group-hover:blur-xl transition-all duration-300 opacity-75" />
                      <span className="relative flex flex-col items-center justify-center w-full px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium shadow-lg hover:shadow-primary/50 transition-all duration-300">
                        <span className="flex items-center gap-2">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            className="w-5 h-5"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {sectionCTAs[currentSection as keyof typeof sectionCTAs]}
                        </span>
                        <span className="text-xs opacity-90 mt-0.5 font-normal tracking-wide">{sectionSocialProof[currentSection as keyof typeof sectionSocialProof]}</span>
                      </span>
                    </motion.button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Download Form Modal */}
      <DynamicDownloadForm 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        projectId={projectId}
        language={language}
        content={downloadFormContent}
        onSuccess={handleFormSuccess}
      />
    </>
  );
} 